import React, { useState } from "react";
import { ArrayInput, SimpleFormIterator, ReferenceInput, AutocompleteInput, FormDataConsumer } from "react-admin";
import { Grid, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { useTranslate, useSimpleFormIteratorItem } from "react-admin";
import { Tooltip, IconButton } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import dataProvider from "server/dataProvider";
import { useWatch } from "react-hook-form";
import { settings } from "app";
import { ClassTypes } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";

const roomOptionText = rec =>
  `${rec.roomName} - ${rec.buildingName}${rec.capacityExam ? ` (${rec.capacityExam})` : ""}`;

const RoomsList = ({ isDisabled, readOnly }) => {
  const notifyError = useNotifyError();
  const [roomsCapacity, setRoomsCapacity] = useState(null);
  const mainRooms = useWatch({ name: "rooms" });
  const mainRoomsIds = mainRooms?.map(room => room.roomId).filter(Boolean) || [];
  const [excludedRoomsIds, setExcludedRoomsIds] = useState([]);
  const [roomCurrentIndex, setRoomCurrentIndex] = useState(null);

  const fetchRoomsCapacity = async (roomIds, classTypes, showExamCapacity) => {
    if (roomIds.length === 0) {
      setRoomsCapacity(null);
      return;
    }

    try {
      const { data } = await dataProvider.customRequest(
        administrationRequests.getRoomsCapacitiesSelect(roomIds, classTypes, showExamCapacity),
        null,
        "GET",
      );

      setRoomsCapacity(data);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleRoomChange = async (event, index, classTypes, showExamCapacity) => {
    const updatedExcludedRoomsIds = [...(excludedRoomsIds || [])];
    updatedExcludedRoomsIds[index] = event;
    setExcludedRoomsIds(updatedExcludedRoomsIds);

    await fetchRoomsCapacity(updatedExcludedRoomsIds.filter(Boolean), classTypes, showExamCapacity);
  };

  const handleRemoveRoom = async (removedId, classTypes, showExamCapacity) => {
    const updatedExcludedRoomsIds = excludedRoomsIds.filter(id => id !== removedId);
    setExcludedRoomsIds(updatedExcludedRoomsIds);

    await fetchRoomsCapacity(updatedExcludedRoomsIds, classTypes, showExamCapacity);
  };

  const RemoveRoomButton = ({ record, classTypes, showExamCapacity }) => {
    const translate = useTranslate();
    const { remove } = useSimpleFormIteratorItem();

    const handleClick = () => {
      if (record.roomId) {
        handleRemoveRoom(record.roomId, classTypes, showExamCapacity);
      }
      remove();
    };
    const title = translate("ra.action.remove");
    return (
      <Tooltip title={title}>
        <IconButton
          color="warning"
          onClick={handleClick}
        >
          <RemoveCircleOutlineIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const RoomIndexComponent = () => {
    const { index } = useSimpleFormIteratorItem();
    setRoomCurrentIndex(index);
  };

  return (
    <>
      <Grid
        item
        container
      >
        <Grid
          item
          xs={12}
          md={12}
        >
          <Typography color={"primary"}>Зали</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
        >
          <ArrayInput
            source="rooms"
            label=""
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableRemove
              disableAdd={isDisabled || readOnly}
              disableReordering={isDisabled || readOnly}
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ scopedFormData }) => {
                  return (
                    <Grid
                      container
                      spacing={1}
                    >
                      <Grid
                        item
                        xs={12}
                        md={10}
                      >
                        <RoomIndexComponent />
                        <ReferenceInput
                          source="roomId"
                          reference={administrationRequests.getRoomsSelect()}
                          filter={{
                            excludeIds: excludedRoomsIds.filter(Boolean),
                            ...(mainRoomsIds?.length ? { ids: mainRoomsIds } : {}),
                            ClassTypes: ClassTypes.Csc,
                          }}
                        >
                          <AutocompleteInput
                            label="Зала"
                            optionValue="id"
                            optionText={roomOptionText}
                            inputText={roomOptionText}
                            matchSuggestion={() => true}
                            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                            onChange={event => handleRoomChange(event, roomCurrentIndex, 1, true)}
                            fullWidth
                            readOnly={isDisabled || readOnly}
                          />
                        </ReferenceInput>
                      </Grid>
                      {!isDisabled && !readOnly && (
                        <Grid
                          item
                          xs={12}
                          md={1}
                        >
                          <RemoveRoomButton
                            record={scopedFormData}
                            classTypes={ClassTypes.Csc}
                            showExamCapacity={true}
                          />
                        </Grid>
                      )}
                    </Grid>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
      {roomsCapacity && (
        <Grid
          item
          xs={12}
          md={12}
          sx={{ paddingBottom: 5 }}
        >
          <Typography
            color="secondary"
            variant="h6"
          >
            Общ капацитет на залите: {roomsCapacity}
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default RoomsList;
